/**
 * Breakpoint and max-content-width utilities
 * Matches SASS variables in redesign/vars/_variables.scss
 */

// ================================================
// BREAKPOINT CONSTANTS
// ================================================

const BREAKPOINT_LARGE = 1024;
const BREAKPOINT_MEDIUM = 820;
const BREAKPOINT_SMALL = 640;
const BREAKPOINT_EXTRA_SMALL = 380;
const BREAKPOINT_LEGACY_NAVIGATION = 767;
const BREAKPOINT_NAVIGATION = getComputedStyle(document.documentElement).getPropertyValue('--site-width') ? BREAKPOINT_LARGE : BREAKPOINT_LEGACY_NAVIGATION;


// ================================================
// MAX CONTENT WIDTH CONSTANTS
// ================================================

const BREAKPOINT_MAX_WIDTH_SMALL = 1200;
const BREAKPOINT_MAX_WIDTH_MEDIUM = 1440;
const BREAKPOINT_MAX_WIDTH_LARGE = 1920;


// ================================================
// UTILITY FUNCTIONS
// ================================================

/**
 * Gets the current max-content-width based on HTML data-width attribute
 * Matches the behavior of the SASS mixins in _mixins.scss
 * 
 * @param {Document|Element} [doc=document] - Document or element to check
 * @returns {number} The max-content-width value (1200, 1440, or 1920)
 * 
 * @example
 * const width = BREAKPOINT_MAX_WIDTH(); // Returns based on data-width attribute
 */
function BREAKPOINT_MAX_WIDTH(doc = document) {
  const htmlElement = doc.documentElement || doc;
  const dataWidth = htmlElement.getAttribute('data-width');
  
  if (dataWidth === 'large') {
    return BREAKPOINT_MAX_WIDTH_LARGE;
  }
  
  if (dataWidth === 'medium') {
    return BREAKPOINT_MAX_WIDTH_MEDIUM;
  }
  
  if (dataWidth === 'small') {
    return BREAKPOINT_MAX_WIDTH_SMALL;
  }
  
  return BREAKPOINT_MAX_WIDTH_SMALL;
}


/**
 * Creates a MediaQueryList for a min-width query (above breakpoint)
 * Matches the behavior of the SASS @mixin above() - adds 1px to breakpoint
 * 
 * @param {number} breakpoint - The breakpoint value in pixels
 * @returns {MediaQueryList} A MediaQueryList object for the min-width query
 * 
 * @example
 * const desktopQuery = above(BREAKPOINT_LARGE);
 * desktopQuery.addEventListener('change', (e) => {
 *   if (e.matches) {
 *     // Desktop view (viewport >= 1025px)
 *   }
 * });
 */
function above(breakpoint) {
  return window.matchMedia(`(min-width: ${breakpoint + 1}px)`);
}

/**
 * Creates a MediaQueryList for a max-width query (below breakpoint)
 * Matches the behavior of the SASS @mixin below()
 * 
 * @param {number} breakpoint - The breakpoint value in pixels
 * @returns {MediaQueryList} A MediaQueryList object for the max-width query
 * 
 * @example
 * const mobileQuery = below(BREAKPOINT_LARGE);
 * mobileQuery.addEventListener('change', (e) => {
 *   if (e.matches) {
 *     // Mobile view (viewport <= 1024px)
 *   }
 * });
 */
function below(breakpoint) {
  return window.matchMedia(`(max-width: ${breakpoint}px)`);
}

/**
 * Creates a MediaQueryList for a between breakpoints query
 * Matches the behavior of the SASS @mixin between() - automatically determines min/max
 * 
 * @param {number} breakpoint1 - The first breakpoint value in pixels
 * @param {number} breakpoint2 - The second breakpoint value in pixels
 * @returns {MediaQueryList} A MediaQueryList object for the between query
 * 
 * @example
 * const tabletQuery = between(BREAKPOINT_SMALL, BREAKPOINT_LARGE);
 * tabletQuery.addEventListener('change', (e) => {
 *   if (e.matches) {
 *     // Tablet view (641px <= viewport <= 1024px)
 *   }
 * });
 */
function between(breakpoint1, breakpoint2) {
  const min = Math.min(breakpoint1, breakpoint2);
  const max = Math.max(breakpoint1, breakpoint2);
  return window.matchMedia(`(min-width: ${min + 1}px) and (max-width: ${max}px)`);
}

/**
 * Gets the current variant name based on HTML data-width attribute
 * 
 * @param {Document|Element} [doc=document] - Document or element to check
 * @returns {string} The variant name ('small', 'medium', or 'large')
 * 
 * @example
 * if (VIEWPORT_WIDTH() === 'large') {
 *   // Do something specific for data-width="large"
 * }
 */
function VIEWPORT_WIDTH(doc = document) {
  const htmlElement = doc.documentElement || doc;
  const dataWidth = htmlElement.getAttribute('data-width');
  
  if (dataWidth === 'large') {
    return 'large';
  }
  
  if (dataWidth === 'medium') {
    return 'medium';
  }
  
  if (dataWidth === 'small') {
    return 'small';
  }
  
  return 'small';
}

// ================================================
// DEFAULT EXPORT
// ================================================

export default {
  BREAKPOINT_LARGE,
  BREAKPOINT_MEDIUM,
  BREAKPOINT_SMALL,
  BREAKPOINT_EXTRA_SMALL,
  BREAKPOINT_NAVIGATION,
  BREAKPOINT_MAX_WIDTH_SMALL,
  BREAKPOINT_MAX_WIDTH_MEDIUM,
  BREAKPOINT_MAX_WIDTH_LARGE,
  above,
  below,
  between,
  BREAKPOINT_MAX_WIDTH,
  VIEWPORT_WIDTH,
};
